import React, { FC, useId } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import {
  IconWrapper,
  SELECT_BUTTON_IS_SELECTED_CLASS,
  SelectButtonLabel,
} from '@rover/components/src/buttons/SelectButton/SelectButton';
import ServiceTypeFilter from '@rover/components/src/formFields/ServiceTypeFilter';
import Label from '@rover/components/src/typography/Label';
import Box from '@rover/kibble/core/Box';
import Flex from '@rover/kibble/core/Flex';
import A11yHidden from '@rover/kibble/styles/A11yHidden';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';

import { UpdateServiceType } from '../../../utilities/analyticsEvents';
import type { UpdateFiltersFunc } from '../UpdateFiltersFunc';

const StyledServiceTypeFilter = styled(ServiceTypeFilter)`
  min-height: 64px;
  margin-bottom: 12px;

  ${IconWrapper},
  svg {
    min-width: unset;
    max-width: unset;
    min-height: unset;
    max-height: unset;
    margin-bottom: 0;
    height: 20px;
    width: 20px;
  }

  svg {
    fill: ${({ theme }) => theme.textColor.tertiary};
  }

  ${SelectButtonLabel} {
    padding: 8px;

    &:hover {
      svg {
        fill: ${({ theme }) => theme.textColor.secondary};
      }
    }
    &.${SELECT_BUTTON_IS_SELECTED_CLASS} {
      color: ${({ theme }) => theme.textColor.primary};
      svg {
        fill: ${({ theme }) => theme.textColor.primary};
      }
    }
  }
`;

const A11yHiddenLabel = styled.label`
  ${A11yHidden};
`;

export interface Props {
  filters: HomepageSearchFilters;
  serviceTypeOptions: ServiceTypeOption[];
  updateFilters: UpdateFiltersFunc;
  onInteract?: () => void;
}

const ServiceTypeConnector: FC<Props> = ({
  filters,
  serviceTypeOptions,
  updateFilters,
  onInteract,
}: Props) => {
  const { t } = useLingui();
  const fieldId = useId();

  return (
    <Flex width="100%" flexDirection="column">
      <Label aria-hidden="true">{t`What kind of pet care are you looking for?`}</Label>
      <Box>
        <A11yHiddenLabel id={fieldId}>{t`Service type`}</A11yHiddenLabel>
        <StyledServiceTypeFilter
          displayMode="horizontal"
          serviceTypeOptions={serviceTypeOptions}
          serviceTypeFilterId={fieldId}
          searchFilters={{ serviceType: filters.serviceTypeSlug }}
          onChange={({ serviceType: serviceTypeSlug }) => {
            onInteract?.();
            emitAnalyticsEvent(new UpdateServiceType({ serviceType: serviceTypeSlug }));
            updateFilters({ serviceTypeSlug });
          }}
        />
      </Box>
    </Flex>
  );
};

export default ServiceTypeConnector;
